import { useEffect, useState, useRef } from "react";
import { Eye, EyeOff, Mail } from 'lucide-react';
import { SocialLogin } from "@capgo/capacitor-social-login";
import { auth, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, onAuthStateChanged, sendEmailVerification, sendPasswordResetEmail, GoogleAuthProvider, signInWithCredential } from "./firebase";
import { createProTrial } from "@/services/trialService";

// Web OAuth Client ID de Google (client_type: 3 en google-services.json).
// Debe pasarse explícitamente a SocialLogin.initialize(): esta versión del
// plugin no lee capacitor.config.ts automáticamente, solo los parámetros
// recibidos en la propia llamada JS.
const GOOGLE_WEB_CLIENT_ID = "642970946255-ss7lp9phha6jmd2m7khs0pkv46cbjuat.apps.googleusercontent.com";

const rutappLogo = "/images/rutapp-logo.png.png";

// 🔓 BYPASS DE LOGIN TEMPORAL - Para desarrollo
// Para REACTIVAR el login: Cambiar BYPASS_LOGIN_TEMPORARILY a false (línea 9)
const BYPASS_LOGIN_TEMPORARILY = false;

// Usuario mock para bypass - reemplazar cuando se reactive el login
const MOCK_USER = {
  uid: 'dev-mock-user-12345',
  email: 'test@development.local',
  emailVerified: false
};

export default function AuthGate({ children }) {
  const [user, setUser] = useState(BYPASS_LOGIN_TEMPORARILY ? MOCK_USER : null);
  const [loading, setLoading] = useState(!BYPASS_LOGIN_TEMPORARILY);
  const [showForm, setShowForm] = useState(false);
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
   const [error, setError] = useState("");
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [minSplashTimeElapsed, setMinSplashTimeElapsed] = useState(false);
   const [forgotPassword, setForgotPassword] = useState(false);
   const [verificationSent, setVerificationSent] = useState(false);
   const [submittedEmail, setSubmittedEmail] = useState("");
   const [emailNotVerified, setEmailNotVerified] = useState(false);
   const [successMessage, setSuccessMessage] = useState("");
   const [showPass, setShowPass] = useState(false);
   const autoLoginAttemptedRef = useRef(false);
   // Guarda la Promise de inicialización de SocialLogin (no un booleano) para
   // que handleGoogleSignIn() pueda esperarla de forma reutilizable y
   // detectar si falló, evitando también inicializaciones duplicadas.
   const googleInitPromiseRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setMinSplashTimeElapsed(true);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (BYPASS_LOGIN_TEMPORARILY) return;
    if (googleInitPromiseRef.current) return;

    googleInitPromiseRef.current = SocialLogin.initialize({
      google: {
        webClientId: GOOGLE_WEB_CLIENT_ID,
        mode: 'online',
      },
    }).catch((err) => {
      console.error("[AUTH_GATE] Error inicializando SocialLogin (Google):", err);
      throw err;
    });
  }, []);

  useEffect(() => {
    console.log("[AUTH_GATE] mounted");
    
    // 🔓 Si BYPASS_LOGIN_TEMPORARILY está activo, no hacer nada con Firebase Auth
    if (BYPASS_LOGIN_TEMPORARILY) {
      console.log("[AUTH_GATE] BYPASS MODE ACTIVE - Saltando Firebase Auth");
      return () => {};
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log("[AUTH_GATE] onAuthStateChanged triggered", { user: !!user });
      
      // SIEMPRE actualizar estado: user puede ser null o un objeto usuario
      setUser(user);
      setLoading(false);
      
      if (user) {
        console.log(`✅ Usuario conectado: ${user.uid}`);
      } else {
        console.log("❌ Usuario desconectado");
        
        // 🔐 AUTO-LOGIN EN MODO DESARROLLO
        if (
          import.meta.env.DEV &&
          import.meta.env.VITE_DEV_AUTO_LOGIN === "true" &&
          !autoLoginAttemptedRef.current
        ) {
          autoLoginAttemptedRef.current = true;
          const devEmail = import.meta.env.VITE_DEV_AUTO_LOGIN_EMAIL;
          const devPassword = import.meta.env.VITE_DEV_AUTO_LOGIN_PASSWORD;
          
          if (devEmail && devPassword) {
            console.log("[DEV_AUTO_LOGIN] attempting...");
            try {
              await signInWithEmailAndPassword(auth, devEmail, devPassword);
              console.log("[DEV_AUTO_LOGIN] success");
              // onAuthStateChanged se disparará automáticamente con el nuevo usuario
            } catch (err) {
              console.warn("[DEV_AUTO_LOGIN] failed:", err.message);
              // No romper nada, dejar que el usuario vea el formulario de login
            }
          }
        }
      }
    });

    return () => {
      console.log("[AUTH_GATE] cleanup");
      unsubscribe();
    };
  }, []);

   async function handleSubmit(e) {
     e.preventDefault();
     setError("");
     setSuccessMessage("");
     setEmailNotVerified(false);
     setIsSubmitting(true);

     try {
         if (isRegister) {
          console.log("🚀 Registrando nuevo usuario...");
          const cred = await createUserWithEmailAndPassword(auth, email.trim(), pass);
          console.log("✅ Usuario registrado exitosamente");

          // 🎁 Crear trial Pro 60 días (non-blocking — si falla no rompe el registro)
          await createProTrial(cred.user.uid, cred.user.email);

          // 📧 Enviar email de verificación
          console.log("📧 Enviando email de verificación...");
          await sendEmailVerification(cred.user);
          console.log("✅ Email de verificación enviado");
         
         // 🔓 Desconectar hasta que verifique
         await signOut(auth);
         console.log("🔓 Usuario desconectado hasta verificar email");
         
         setSubmittedEmail(email.trim());
         setVerificationSent(true);
         setShowForm(false);
         setEmail("");
         setPass("");
       } else {
         console.log("🚀 Iniciando sesión...");
         const cred = await signInWithEmailAndPassword(auth, email.trim(), pass);
         console.log("✅ Sesión iniciada exitosamente");
         
         // 🔄 Recargar estado del usuario desde Firebase
         await cred.user.reload();
         
         if (!cred.user.emailVerified) {
           console.warn("⚠️ Email no verificado");
           await signOut(auth);
           setEmailNotVerified(true);
           setError("");
           return;
         }
         
         setShowForm(false);
         setEmail("");
         setPass("");
       }
     } catch (err) {
       console.error("❌ Error de autenticación:", err.message);
       setError(getErrorMessage(err.code));
     } finally {
       setIsSubmitting(false);
     }
   }

   async function handleGoogleSignIn() {
     setError("");
     setSuccessMessage("");
     setEmailNotVerified(false);
     setIsSubmitting(true);

     try {
       // Esperar explícitamente a que SocialLogin.initialize() haya
       // finalizado antes de intentar el login. Si initialize() falló,
       // este await relanza ese mismo error y cae al catch genérico de abajo.
       if (googleInitPromiseRef.current) {
         await googleInitPromiseRef.current;
       }

       console.log("🚀 Iniciando sesión con Google...");
       const { result } = await SocialLogin.login({
         provider: 'google',
         options: {},
       });

       // La respuesta 'offline' solo trae serverAuthCode (sin idToken). En
       // modo 'online' (configurado en initialize) siempre debe venir
       // idToken. Si no aparece donde se espera, no se improvisa: se detiene.
       if (result.responseType !== 'online' || !result.idToken) {
         console.error("[AUTH_GATE] Respuesta de Google sin idToken esperado:", result);
         setError("No se pudo completar el acceso con Google. Inténtalo de nuevo.");
         return;
       }

       const credential = GoogleAuthProvider.credential(result.idToken, result.accessToken?.token ?? null);
       const cred = await signInWithCredential(auth, credential);
       console.log("✅ Sesión con Google iniciada exitosamente");

       // 🎁 Trial Pro — realmente no bloqueante: se lanza y su error se
       // captura de forma independiente, sin await, para que un fallo del
       // trial nunca convierta este login (ya autenticado) en un fallo ni
       // dispare el catch/signOut de este handler.
       createProTrial(cred.user.uid, cred.user.email).catch((trialError) => {
         console.error("[AUTH_GATE] Error creando trial Pro tras login Google (no bloqueante):", trialError);
       });

       // No se hace setUser manual: onAuthStateChanged ya gestiona la sesión.
       setShowForm(false);
       setEmail("");
       setPass("");
     } catch (err) {
       console.error("❌ Error de autenticación con Google:", err);
       if (err?.code === 'auth/account-exists-with-different-credential') {
         setError("Ya existe una cuenta con este correo. Inicia sesión con tu correo y contraseña.");
       } else {
         setError("No se pudo completar el acceso con Google. Inténtalo de nuevo.");
       }
     } finally {
       setIsSubmitting(false);
     }
   }

   async function handleLogout() {
     console.log("🚀 Cerrando sesión...");
     try {
       await SocialLogin.logout({ provider: 'google' });
       console.log("✅ Sesión nativa de Google cerrada");
     } catch (err) {
       console.warn("⚠️ No se pudo cerrar sesión nativa de Google (no bloqueante):", err);
     }
     await signOut(auth);
     console.log("✅ Sesión cerrada");
   }

   async function handleForgotPassword(e) {
     e.preventDefault();
     setError("");
     setIsSubmitting(true);

     try {
       if (!email.trim()) {
         setError("Por favor introduce tu email");
         setIsSubmitting(false);
         return;
       }
       console.log("📧 Enviando email de recuperación de contraseña...");
       await sendPasswordResetEmail(auth, email.trim());
        console.log("✅ Email de recuperación enviado");
        setError("");
        setForgotPassword(false);
        setShowForm(false);
        setEmail("");
        setPass("");
        // Mostrar confirmación brevemente
        setSuccessMessage("Revisa tu email para recuperar tu contraseña");
     } catch (err) {
       console.error("❌ Error al enviar email de recuperación:", err.message);
       setError(getErrorMessage(err.code));
     } finally {
       setIsSubmitting(false);
     }
   }

   async function handleResendVerificationEmail(e) {
     e.preventDefault();
     setError("");
     setIsSubmitting(true);

     try {
       if (!email.trim()) {
         setError("Por favor introduce tu email");
         setIsSubmitting(false);
         return;
       }
       console.log("📧 Reenviando email de verificación...");
       // Intentar iniciar sesión para obtener el usuario
       const cred = await signInWithEmailAndPassword(auth, email.trim(), pass);
       await sendEmailVerification(cred.user);
       await signOut(auth);
       console.log("✅ Email de verificación reenviado");
       setError("");
       setEmailNotVerified(false);
       setEmail("");
       setPass("");
     } catch (err) {
       console.error("❌ Error al reenviar email de verificación:", err.message);
       setError(getErrorMessage(err.code));
     } finally {
       setIsSubmitting(false);
     }
   }

  function getErrorMessage(errorCode) {
    switch (errorCode) {
      case 'auth/user-not-found': return 'Usuario no encontrado';
      case 'auth/wrong-password': return 'Contraseña incorrecta';
      case 'auth/email-already-in-use': return 'El email ya está registrado';
      case 'auth/weak-password': return 'La contraseña debe tener al menos 6 caracteres';
      case 'auth/invalid-email': return 'Email inválido';
      default: return 'Error de autenticación';
    }
  }

   if (!minSplashTimeElapsed) {
     console.log("[AUTH_GATE] render splash");
     return (
       <div className="w-full h-screen flex flex-col items-center justify-center gap-4 bg-black">
        <img
          src={rutappLogo}
          alt="Rutapp"
          className="h-28 w-auto opacity-90"
        />
        <p style={{color: '#B0B0B0'}}>Cargando…</p>
      </div>
    );
  }

  if (!user) {
    console.log("[AUTH_GATE] render login");
  }

  if (user) {
    console.log("[AUTH_GATE] render app");
  }

  return (
    <>
      {user && (
        <div style={{padding: '16px'}}>
          {children}
        </div>
      )}

      {!user && (
        <>
          {error && (
            <div style={{
              padding: '12px 16px',
              background: '#7f1d1d',
              borderLeft: '4px solid #dc2626',
              color: '#fecaca'
            }}>
              ❌ {error}
            </div>
          )}

          {successMessage && (
            <div style={{
              padding: '12px 16px',
              background: '#14532d',
              borderLeft: '4px solid #16a34a',
              color: '#bbf7d0'
            }}>
              ✅ {successMessage}
              <p
  style={{
    fontSize: '15px',
    color: '#B0B0B0',
    marginBottom: '16px',
    fontWeight: 500
  }}
>
  Si no lo ves en unos minutos, revisa también la carpeta de spam.
</p>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center items-center h-screen flex-col gap-4 bg-[#121212]" style={{
              color: '#FFFFFF'
            }}>
              <div style={{fontSize: '24px'}}>🔄</div>
              <div>Verificando autenticación...</div>
            </div>
          ) : (
            <div className="bg-[#121212] min-h-screen" style={{
              padding: '64px 16px',
              textAlign: 'center',
              color: '#EAEAEA'
            }}>
              <img
                src={rutappLogo}
                alt="Rutapp"
                style={{ height: '80px', width: 'auto', marginBottom: '24px', display: 'block', margin: '0 auto 24px auto' }}
              />

              {/* PANTALLA: Verificación enviada */}
              {verificationSent && (
                <>
                  <div style={{fontSize: '48px', marginBottom: '16px'}}>📧</div>
                  <h2 style={{marginBottom: '12px'}}>Verifica tu email</h2>
                  <p style={{marginBottom: '24px'}}>Hemos enviado un enlace de verificación a:<br /><strong>{submittedEmail}</strong></p>
                  <p style={{marginBottom: '24px', fontSize: '14px', color: '#B0B0B0'}}>Haz clic en el enlace del email para activar tu cuenta. Luego podrás iniciar sesión.</p>
                  <p
  style={{
    fontSize: '15px',
    color: '#B0B0B0',
    marginBottom: '16px',
    fontWeight: 500
  }}
>
  Si no lo ves en unos minutos, revisa también la carpeta de spam.
</p>
                  <button
                    onClick={() => {
                      setVerificationSent(false);
                      setIsRegister(false);
                      setShowForm(true);
                    }}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '6px',
                      border: '1px solid #3b82f6',
                      background: '#3b82f6',
                      color: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    Volver a Iniciar Sesión
                  </button>
                </>
              )}

              {/* PANTALLA: Email no verificado (usuario antiguo) */}
              {emailNotVerified && (
                <>
                  <div style={{fontSize: '48px', marginBottom: '16px'}}>⚠️</div>
                  <h2 style={{marginBottom: '12px'}}>Email no verificado</h2>
                  <p style={{marginBottom: '24px'}}>Por favor verifica tu email antes de continuar.</p>
                  <button
                    onClick={(e) => handleResendVerificationEmail(e)}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '6px',
                      border: '1px solid #059669',
                      background: '#059669',
                      color: 'white',
                      cursor: 'pointer',
                      marginRight: '8px'
                    }}
                  >
                    Reenviar verificación
                  </button>
                  <button
                    onClick={() => setEmailNotVerified(false)}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '6px',
                      border: '1px solid #4B5563',
                      background: 'transparent',
                      color: '#B0B0B0',
                      cursor: 'pointer'
                    }}
                  >
                    Volver
                  </button>
                </>
              )}

              {/* PANTALLA: Formulario normal (login/registro o recuperar contraseña) */}
              {!verificationSent && !emailNotVerified && (
                <>
                  <div style={{fontSize: '48px', marginBottom: '16px'}}>🔒</div>
                  <h2 style={{marginBottom: '8px'}}>Acceso Restringido</h2>
                  <p>Debes iniciar sesión para acceder a la aplicación.</p>
                  
                  <div style={{marginTop: '24px'}}>
                    {!forgotPassword && (
                      <>
                        <button
                          onClick={handleGoogleSignIn}
                          disabled={isSubmitting}
                          style={{
                            padding: '8px 16px',
                            borderRadius: '6px',
                            border: '1px solid #D1D5DB',
                            background: '#FFFFFF',
                            color: '#111827',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                            cursor: isSubmitting ? 'not-allowed' : 'pointer',
                            opacity: isSubmitting ? 0.7 : 1,
                            marginBottom: '12px',
                            marginLeft: 'auto',
                            marginRight: 'auto',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '10px'
                          }}
                        >
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                          </svg>
                          Entrar con Google
                        </button>

                        <p style={{margin: '0 0 12px 0', fontSize: '12px', color: '#808080'}}>o</p>

                        <button 
                          onClick={() => { setShowForm(v => !v); setSuccessMessage(""); }}
                          style={{
                            padding: '8px 16px',
                            borderRadius: '6px',
                            border: '1px solid #d1d5db',
                            background: 'white',
                            color: '#374151',
                            cursor: 'pointer',
                            marginBottom: '16px',
                            marginLeft: 'auto',
                            marginRight: 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                          }}
                        >
                          {!showForm && <Mail size={18} />}
                          {showForm ? 'Cancelar' : 'Entrar con correo electrónico'}
                        </button>
                        
                        {showForm && (
                          <div style={{
                            display: 'flex',
                            gap: '12px',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexWrap: 'wrap'
                          }}>
                            <form onSubmit={handleSubmit} style={{
                              display: 'flex',
                              gap: '8px',
                              alignItems: 'center',
                              flexWrap: 'wrap',
                              justifyContent: 'center'
                            }}>
                              <input 
                                value={email} 
                                onChange={e => setEmail(e.target.value)} 
                                type="email" 
                                placeholder="email@ejemplo.com" 
                                required
                                style={{
                                  padding: '8px 12px',
                                  borderRadius: '4px',
                                  border: '1px solid #2A2A2A',
                                  minWidth: '200px',
                                  backgroundColor: '#1E1E1E',
                                  color: '#FFFFFF'
                                }}
                              />
                              <div style={{ position: 'relative', display: 'inline-block' }}>
                                <input 
                                  value={pass} 
                                  onChange={e => setPass(e.target.value)} 
                                  type={showPass ? "text" : "password"} 
                                  placeholder="contraseña" 
                                  required
                                  style={{
                                    padding: '8px 12px',
                                    paddingRight: '36px',
                                    borderRadius: '4px',
                                    border: '1px solid #2A2A2A',
                                    minWidth: '150px',
                                    backgroundColor: '#1E1E1E',
                                    color: '#FFFFFF'
                                  }}
                                />
                                <button
                                  type="button"
                                  onClick={() => setShowPass(v => !v)}
                                  style={{
                                    position: 'absolute',
                                    right: '8px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: '#B0B0B0',
                                    padding: '0',
                                    display: 'flex',
                                    alignItems: 'center'
                                  }}
                                >
                                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                              </div>
                              <button 
                                type="submit" 
                                disabled={isSubmitting}
                                style={{
                                  padding: '8px 16px',
                                  borderRadius: '4px',
                                  border: 'none',
                                  background: isRegister ? '#059669' : '#3b82f6',
                                  color: 'white',
                                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                  opacity: isSubmitting ? 0.7 : 1
                                }}
                              >
                                {isSubmitting ? '...' : (isRegister ? 'Registrarse' : 'Entrar')}
                              </button>
                            </form>
                            
                            <button
                              type="button"
                              onClick={() => { setIsRegister(v => !v); setSuccessMessage(""); }}
                              style={{
                                padding: '6px 12px',
                                borderRadius: '4px',
                                border: '1px solid #4B5563',
                                background: 'transparent',
                                color: '#B0B0B0',
                                cursor: 'pointer',
                                fontSize: '14px'
                              }}
                            >
                              {isRegister ? '¿Ya tienes cuenta?' : '¿Nuevo usuario?'}
                            </button>

                            {!isRegister && (
                              <button
                                type="button"
                                onClick={() => {
                                  setForgotPassword(true);
                                  setShowForm(false);
                                  setEmail("");
                                  setPass("");
                                }}
                                style={{
                                  padding: '6px 12px',
                                  borderRadius: '4px',
                                  border: '1px solid #4B5563',
                                  background: 'transparent',
                                  color: '#B0B0B0',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                              >
                                ¿Olvidaste tu contraseña?
                              </button>
                            )}
                          </div>
                        )}
                      </>
                    )}

                    {forgotPassword && (
                      <div style={{marginTop: '24px'}}>
                        <h3 style={{marginBottom: '16px'}}>Recuperar contraseña</h3>
                        <form onSubmit={handleForgotPassword} style={{
                          display: 'flex',
                          gap: '8px',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          justifyContent: 'center'
                        }}>
                          <input 
                            value={email} 
                            onChange={e => setEmail(e.target.value)} 
                            type="email" 
                            placeholder="email@ejemplo.com" 
                            required
                            style={{
                              padding: '8px 12px',
                              borderRadius: '4px',
                              border: '1px solid #2A2A2A',
                              minWidth: '200px',
                              backgroundColor: '#1E1E1E',
                              color: '#FFFFFF'
                            }}
                          />
                          <button 
                            type="submit" 
                            disabled={isSubmitting}
                            style={{
                              padding: '8px 16px',
                              borderRadius: '4px',
                              border: 'none',
                              background: '#d97706',
                              color: 'white',
                              cursor: isSubmitting ? 'not-allowed' : 'pointer',
                              opacity: isSubmitting ? 0.7 : 1
                            }}
                          >
                            {isSubmitting ? '...' : 'Enviar'}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setForgotPassword(false);
                              setShowForm(true);
                              setEmail("");
                              setPass("");
                              setSuccessMessage("");
                            }}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '4px',
                              border: '1px solid #4B5563',
                              background: 'transparent',
                              color: '#B0B0B0',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            Volver
                          </button>
                        </form>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}
    </>
  );
}
